import React, { useCallback, useContext } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import type { UserContextValue } from '@core/context';
import { UserContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useEnvInfo from '@core/hooks/useEnvInfo';
import useIntercom from '@core/hooks/useIntercom';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { isGuidesPage, isReferencePage } from '@core/store/SuperHub/Document/util';
import { isBranch, isVersion } from '@core/store/SuperHub/util';

import { SuperHubHashRoutePaths, type SuperHubHashRouteParams } from '@routes/SuperHub/types';

import Icon from '@ui/Icon';
import Segment, { SegmentItem } from '@ui/Segment';
import Truncate from '@ui/Truncate';

import BaseNav from '../BaseNav';
import BasePanel from '../BasePanel';
import { useSuperHubContext } from '../Context';
import LaunchButton from '../PlanAccess/LaunchButton';
import Transition from '../Transition';

import BranchStatusIndicator from './BranchStatusIndicator';
import ProjectNavigationButton from './Button';
import classes from './index.module.scss';

const leafSegments: Record<
  NonNullable<Extract<SuperHubHashRouteParams['section'], 'custom-components' | 'reusable-content'>>,
  {
    label: string;
  }
> = {
  'reusable-content': {
    label: 'Reusable Content',
  },
  'custom-components': {
    label: 'Custom Components',
  },
};

interface ProjectNavigationProps {
  className?: string;
}

function IntercomButton() {
  const bem = useClassy(classes, 'ProjectNavigation');
  const { isGod } = useContext(UserContext) as unknown as UserContextValue;
  const { isProd } = useEnvInfo();

  /**
   * Initialize the internal readme intercom widget to allow superhub users to
   * contact readme support. This is not the same as the project's intercom
   * instance, which is configured by superhub users in the integrations
   * settings. The project intercom instance is disabled for superhub admins and
   * only visible to end users.
   */
  const isIntercomEnabled = isProd ? !isGod : true;

  const triggerIntercom = useIntercom({
    isEnabled: isIntercomEnabled,
    hideDefaultLauncher: true,
    useProjectIntercomId: false,
  });

  const handleTriggerIntercom = useCallback(() => {
    triggerIntercom();
  }, [triggerIntercom]);

  if (!isIntercomEnabled) return null;

  return (
    <ProjectNavigationButton
      aria-label="Help"
      className={bem('-help-button')}
      onClick={handleTriggerIntercom}
      showTooltip
    >
      <Icon name="help" />
    </ProjectNavigationButton>
  );
}

/**
 * Displays the project controls for the SuperHub layout,
 * which show at the root hash route.
 */
export default function ProjectNavigation({ className }: ProjectNavigationProps) {
  const bem = useClassy(classes, 'ProjectNavigation');

  const [layout, slug, doc, isEditing, routeSection, firstPage, isEnterpriseGLP] = useSuperHubStore(s => [
    s.layout,
    s.slug || '',
    s.document.data,
    s.isEditing,
    s.routeSection,
    s.sidebar.findSidebarFirstPage,
    !!s.enterprise?.isGroup && s.routeSection === 'home',
  ]);

  const [name, onboardingCompleted, isTrial] = useProjectStore(store => [
    store.data.name,
    store.data.onboarding_completed,
    store.plan.isTrial,
  ]);
  const { browserRouterHistory } = useSuperHubContext();

  const isEditableSection = ['changelog', 'docs', 'page', 'reference', 'recipes'].includes(routeSection ?? '');
  const isAiLayout = ['ai'].includes(layout);
  const isSettingsLayout = ['settings', 'settings-preview', 'settings-preview-stretch'].includes(layout);
  const isVersionLayout = ['versions'].includes(layout);
  const isMyDevelopers = layout === 'my-developers';

  const handleEdit = useCallback(
    event => {
      event.preventDefault();
      // If the user is navigated to an editable page, we take them to that page in the editor.
      // Otherwise, we take them to the docs section.
      const entrySection = isEditableSection ? routeSection : 'docs';
      browserRouterHistory.push({
        pathname: `/update/${entrySection}${slug ? `/${slug}` : ''}`,
        hash: '#',
      });
    },
    [browserRouterHistory, isEditableSection, routeSection, slug],
  );

  const handlePreview = useCallback(
    event => {
      event.preventDefault();
      const isGuideReferencePage = isGuidesPage(doc) || isReferencePage(doc);
      const redirectSlug = (isGuideReferencePage && doc.type === 'link') || !slug ? firstPage?.()?.slug : slug;
      const redirectPath = [routeSection, redirectSlug].filter(Boolean).join('/');
      browserRouterHistory.push({
        pathname: `/${redirectPath}`,
        hash: '#',
      });
    },
    [browserRouterHistory, doc, firstPage, routeSection, slug],
  );

  const handleMyDevelopers = useCallback(
    event => {
      event.preventDefault();
      const isSetupComplete = onboardingCompleted.logs;

      // Redirect to setup if My Developers setup is not complete
      const hash = !isSetupComplete ? '/my-developers/setup' : '/my-developers';
      // Use browserRouterHistory to navigate in order to trigger an unsaved changes prompt if the
      // editor form is dirty.
      browserRouterHistory.push({
        hash: `#${hash}`,
      });
    },
    [browserRouterHistory, onboardingCompleted.logs],
  );

  // handle settings & ai navigation
  const { hash } = useLocation();
  const match = matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));
  const hashRouteAction = match?.params.action;
  const handleNav = useCallback(
    (action: SuperHubHashRouteParams['action']) => {
      const isActive = hashRouteAction === action || (action === 'configure' && hashRouteAction === 'appearance');
      window.location.hash = isActive ? '/' : action === 'ai' ? '/ai' : '/configure/project-settings';
    },
    [hashRouteAction],
  );

  // versions & branches
  const isViewingBranch = useSuperHubStore(s => isBranch(s.currentBranch));
  const currentBranch = useSuperHubStore(s => s.currentBranch);
  const isActiveVersionHidden = isVersion(currentBranch) && currentBranch.privacy.view === 'hidden';

  // display versioned content under versions
  const hashRouteSection = match?.params.section?.split('/')[0]; // split needed to handle nested paths
  const leafSegment = hashRouteSection ? leafSegments[hashRouteSection] : null;

  return (
    <Transition
      className={className}
      enter={[
        { transform: 'translateY(calc(var(--SuperHub-top-nav-height) * -1))', opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 },
      ]}
      in={layout !== 'aside-standalone'}
    >
      <BasePanel className={bem('&')} label="Project controls">
        <BaseNav className={bem('-container')}>
          <div className={bem('-left')}>
            <>
              <Segment
                circular
                className={bem('-mode-toggle')}
                selectedIndex={isSettingsLayout ? 0 : isVersionLayout ? 1 : undefined}
                size="xs"
              >
                <SegmentItem className={bem('-segmentitem')} onClick={() => handleNav('configure')}>
                  <Icon aria-hidden="true" name="cog" />
                  <Truncate maxWidth="24ch">{name}</Truncate>
                </SegmentItem>
                {!isEnterpriseGLP && (
                  <SegmentItem
                    className={bem('-segmentitem')}
                    to={hashRouteAction === 'versions' ? '#/' : '#/versions'}
                  >
                    <Icon aria-hidden="true" name="git-branch" />
                    <Truncate maxWidth="24ch">{`v${currentBranch.name}`}</Truncate>
                    {!isActiveVersionHidden && !leafSegment && (
                      <BranchStatusIndicator branchType={isViewingBranch ? 'branch' : 'version'} isMinimal />
                    )}
                    {!!leafSegment && <span className={bem('-leaf')}>{leafSegment.label}</span>}
                  </SegmentItem>
                )}
              </Segment>
            </>
          </div>

          <div className={bem('-center')}>
            {!isEnterpriseGLP && (
              <Segment
                circular
                className={bem('-mode-toggle')}
                selectedIndex={isMyDevelopers ? 2 : isEditing ? 1 : 0}
                size="xs"
              >
                <SegmentItem className={bem('-segmentitem')} onClick={handlePreview}>
                  <Icon aria-hidden="true" name="eye" />
                  View
                </SegmentItem>
                <SegmentItem className={bem('-segmentitem')} onClick={handleEdit}>
                  <Icon aria-hidden="true" name="edit" size="sm" />
                  Edit
                </SegmentItem>
                <SegmentItem className={bem('-segmentitem')} onClick={handleMyDevelopers}>
                  <Icon aria-hidden="true" name="users" size="sm" />
                  My Developers
                </SegmentItem>
              </Segment>
            )}
          </div>

          <div className={bem('-right')}>
            {isSettingsLayout || isVersionLayout || isAiLayout ? (
              <>
                {/* Nav when in any settings */}
                <ProjectNavigationButton active={isAiLayout} onClick={() => handleNav('ai')}>
                  <Icon name="sparkles" /> AI
                </ProjectNavigationButton>
                {!!isTrial && <LaunchButton />}
                <ProjectNavigationButton
                  aria-label="Close Settings"
                  icon={<Icon name="x" />}
                  onClick={() => {
                    window.location.hash = '/';
                  }}
                />
              </>
            ) : (
              <>
                {/* Nav in view/edit/my developers */}
                {isEnterpriseGLP ? (
                  <IntercomButton />
                ) : (
                  <>
                    <ProjectNavigationButton active={isAiLayout} onClick={() => handleNav('ai')}>
                      <Icon name="sparkles" /> AI
                    </ProjectNavigationButton>
                    {!!isTrial && <LaunchButton />}
                    <IntercomButton />
                  </>
                )}
              </>
            )}
          </div>
        </BaseNav>
      </BasePanel>
    </Transition>
  );
}
